import { APP_STATE, JOURNEY_STAGE, LEAD_LOST_REASON, LEAD_STATUS } from '../core/constants/enums';

/**
 * The coach Users list palette (its expiry chips), reused for Leads (A6
 * approved design). Full literal class strings only: Tailwind cannot see
 * constructed class names.
 */
export const TONE = Object.freeze({
  redSolid: 'bg-[#A32D2D] text-white',
  red: 'bg-[#FCEBEB] text-[#A32D2D]',
  amber: 'bg-[#FAEEDA] text-[#8A5A00]',
  green: 'bg-[#EAF3DE] text-[#1D7A4F]',
  blue: 'bg-[#E6F1FB] text-[#2A6EB0]',
  grey: 'bg-gray-100 text-gray-700',
  orange: 'bg-light-orange text-[#B8430A]',
});

export const BTN_PRIMARY =
  'px-3 py-1.5 rounded-lg text-sm font-medium border bg-orange-primary text-white border-orange-primary shadow-sm hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed';
export const BTN_SECONDARY =
  'px-3 py-1.5 rounded-lg text-sm font-medium border bg-white text-gray-700 border-gray-300 hover:bg-[#FFF3EB] hover:border-orange-primary/50 disabled:opacity-60 disabled:cursor-not-allowed';
export const SEGMENT_ON = 'bg-orange-primary text-white border-orange-primary shadow-sm';
export const SEGMENT_OFF = 'bg-white text-gray-700 border-gray-300 hover:bg-[#FFF3EB] hover:border-orange-primary/50';
export const CELL = 'px-6 py-4 whitespace-nowrap border-r border-gray-200 text-center';
export const ICON_ACTION = 'p-1.5 rounded-full text-gray-500 hover:text-orange-primary hover:bg-[#FFF3EB]';
export const CHIP = 'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold';
/** Card + section header used across the lead detail page. */
export const CARD = 'bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden';
export const CARD_HEADER = 'flex items-center justify-between gap-2 border-b border-gray-200 bg-light-orange px-5 py-3';
export const CARD_TITLE = 'text-xs font-semibold uppercase tracking-wider text-gray-700';
export const INPUT =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary focus:border-transparent';

/** Status order as shown in the chip dropdown. */
export const STATUS_OPTIONS = Object.freeze([
  { id: LEAD_STATUS.NEW, label: 'New', tone: TONE.orange },
  { id: LEAD_STATUS.CONTACTED, label: 'Contacted', tone: TONE.grey },
  { id: LEAD_STATUS.NO_ANSWER, label: 'No answer', tone: TONE.grey },
  { id: LEAD_STATUS.CALL_BACK, label: 'Call back', tone: TONE.amber },
  { id: LEAD_STATUS.INTERESTED, label: 'Interested', tone: TONE.blue },
  { id: LEAD_STATUS.CONSULT_BOOKED, label: 'Consult booked', tone: TONE.green },
  { id: LEAD_STATUS.NOT_INTERESTED, label: 'Not interested', tone: TONE.grey },
  { id: LEAD_STATUS.JUNK, label: 'Junk', tone: TONE.grey },
]);

export const STATUS_BY_ID = Object.freeze(Object.fromEntries(STATUS_OPTIONS.map((s) => [s.id, s])));

/** Why a lead said no (LEAD_LOST_REASON), in picker order. */
export const LOST_REASON_LABEL = Object.freeze({
  [LEAD_LOST_REASON.PRICE]: 'Price too high',
  [LEAD_LOST_REASON.NO_TIME]: 'Not the right time / no time',
  [LEAD_LOST_REASON.NO_RESULTS]: "Didn't see results",
  [LEAD_LOST_REASON.CHOSE_OTHER]: 'Chose someone else',
  [LEAD_LOST_REASON.MOVED]: 'Moved / relocated',
  [LEAD_LOST_REASON.HEALTH]: 'Health reason',
  [LEAD_LOST_REASON.OTHER]: 'Other',
});

/** Known app-user tags that link to /dashboard/{user_id} (A6, A7c). */
export const APP_TAGS = Object.freeze({
  [APP_STATE.FREE]: { label: 'Free user', tone: TONE.grey, title: 'Free: on the app but never paid.' },
  [APP_STATE.LAPSED]: { label: 'Lapsed', tone: TONE.amber, title: 'Paid before, no active plan.' },
  [APP_STATE.PAYING]: { label: "Paying · don't pitch", tone: TONE.redSolid, title: 'Active paying customer.' },
  [APP_STATE.JOINED]: { label: 'Joined app', tone: TONE.green, title: 'Signed up after this form.' },
  [APP_STATE.PLAN_UNKNOWN]: {
    label: 'On app · plan unknown',
    tone: 'border border-dashed border-gray-400 text-gray-700',
    title: "We couldn't load their plan. Check before you pitch.",
  },
});

/** Journey stage chips (subscription-driven): Form lead → Signed up → Paid → Lapsed. */
export const JOURNEY_STAGES = Object.freeze({
  [JOURNEY_STAGE.FORM_LEAD]: { label: 'Form lead', tone: TONE.grey },
  [JOURNEY_STAGE.SIGNED_UP]: { label: 'Signed up', tone: TONE.blue },
  [JOURNEY_STAGE.PAID]: { label: 'Paid', tone: TONE.green },
  [JOURNEY_STAGE.LAPSED]: { label: 'Lapsed', tone: TONE.amber },
});

/** N3: hollow outline so it can never be mistaken for a paying customer. */
export const UNVERIFIED_TAG = 'inline-flex items-center rounded-full border border-gray-400 px-2 py-0.5 text-[11px] font-semibold text-gray-600';

export const PAYING_MATCH_COPY = "Possible paying customer · don't pitch until checked";
